'use client';

import { AnalyticsBrowser } from '@segment/analytics-next';
import { Check, Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useState } from 'react';
import { Button, Input, Label, TokenSent } from '@/components/era-ui';
import { useAuthActions } from '@/hooks/useAuthActions';
import { Env } from '@/libs/Env';
import { getDefaultInterviewTemplateId } from '@/libs/interviewer/constants';
import { AuthDivider } from './AuthDivider';
import { SignUpFormWrapper } from './FormWrapper';
import { ArrowLeft, SignInHSIcon } from './Images';
import { TermsAndPrivacy } from './TermsAndPrivacy';

// Initialize analytics
const analytics = AnalyticsBrowser.load({ writeKey: process.env.NEXT_PUBLIC_SEGMENT_WRITE_KEY || '' });

const ANALYTICS_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'campaignid',
  'domain',
  'degree',
] as const;

type InterviewTemplate = {
  display_name: string;
  description: string;
  rating: number;
  time_to_complete: number;
};

export const SignUpForm = ({
  authError,
  authLoading,
  email,
  isValidEmail,
  isSignInWithHandshakeEnabled,
  handleEmailChange,
  handleEmailSubmit,
  signInWithGoogle,
  signInWithGitHub,
  signInWithHandshake,
}: {
  authError: string | null;
  authLoading: boolean;
  email: string;
  isValidEmail: boolean;
  isSignInWithHandshakeEnabled: boolean;
  handleEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleEmailSubmit: (e: React.FormEvent) => void;
  signInWithGoogle: () => void;
  signInWithGitHub: () => void;
  signInWithHandshake: () => void;
}) => {
  const t = useTranslations('Auth');

  const [isInEmailView, setIsInEmailView] = useState(false);
  const [isHSButtonHovered, setIsHSButtonHovered] = useState(false);

  if (isInEmailView) {
    return (
      <SignUpFormWrapper
        header={(
          <h2 className="text-center text-header-md gap-8 flex items-center w-full">
            <Button
              variant="secondary"
              onClick={() => setIsInEmailView(false)}
              className="border-none"
            >
              <ArrowLeft />
            </Button>
            {t('sign_up_with_email')}
          </h2>
        )}
      >
        {authError && (
          <div className="rounded-sm bg-red-500/10 p-3 text-sm text-red-500">
            {authError}
          </div>
        )}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '16px',
            alignSelf: 'stretch',
          }}
        >
          <div className="w-full">
            <Label htmlFor="email">
              {t('email')}
            </Label>
            <Input
              type="email"
              id="email"
              value={email}
              onChange={handleEmailChange}
              errorMessage={!isValidEmail ? t('please_enter_valid_email') : undefined}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleEmailSubmit(e);
                }
              }}
            />
          </div>
          <Button variant="primary" type="submit" disabled={authLoading} onClick={handleEmailSubmit} fullWidth>
            {t('continue')}
          </Button>
        </div>
        <TermsAndPrivacy />
      </SignUpFormWrapper>
    );
  }

  return (
    <SignUpFormWrapper title={t('sign_up_to_get_started')}>
      {authError && (
        <div className="rounded-sm bg-red-500/10 p-3 text-sm text-red-500">
          {authError}
        </div>
      )}

      <div className="flex flex-col gap-2 w-full">
        {isSignInWithHandshakeEnabled && (
          <>
            <Button
              variant="primary"
              onClick={signInWithHandshake}
              disabled={authLoading}
              onMouseOver={() => setIsHSButtonHovered(true)}
              onFocus={() => setIsHSButtonHovered(true)}
              onMouseOut={() => setIsHSButtonHovered(false)}
              onBlur={() => setIsHSButtonHovered(false)}
            >
              <SignInHSIcon active={isHSButtonHovered} />
              {t('sign_in_with_handshake')}
            </Button>
            <AuthDivider text={t('or')} />
          </>
        )}
        <Button
          variant="secondary"
          onClick={signInWithGoogle}
          disabled={authLoading}
        >
          <Image
            src="/assets/images/google-logo.svg"
            alt="Google"
            width={16}
            height={16}
          />
          {t('sign_in_with_google')}
        </Button>
        <Button
          variant="secondary"
          onClick={signInWithGitHub}
          disabled={authLoading}
        >
          <Image
            src="/assets/images/github-logo.svg"
            alt="GitHub"
            width={16}
            height={16}
          />
          {t('sign_in_with_github')}
        </Button>
        <Button
          variant="secondary"
          onClick={() => setIsInEmailView(true)}
          disabled={authLoading}
        >
          <Image
            src="/assets/icons/mail-send-envelope.svg"
            alt="Email"
            width={16}
            height={16}
          />
          {t('sign_in_with_email')}
        </Button>
      </div>
      <TermsAndPrivacy />
    </SignUpFormWrapper>
  );
};

function AuthPageV2Content() {
  const isSignInWithHandshakeEnabled = Env.NEXT_PUBLIC_APP_ENVIRONMENT !== 'production';
  const searchParams = useSearchParams();
  const role = searchParams?.get('role') as 'candidate' | 'recruiter' | null;
  const [_template, setTemplate] = useState<InterviewTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const emailParam = searchParams?.get('email');
  const [email, setEmail] = useState(() => emailParam ? decodeURIComponent(emailParam) : '');
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [showLinkSent, setShowLinkSent] = useState(false);

  const redirectTo = searchParams?.get('redirectTo') || '/fellow/dashboard';
  const referralCode = searchParams?.get('referralCode');
  // Track page view with query parameters
  useEffect(() => {
    const queryParams: { [key: string]: string } = {};
    ANALYTICS_KEYS.forEach((key) => {
      queryParams[key] = searchParams?.get(key) || '';
    });
    // Get the referrer page url
    const referrer = document.referrer;
    analytics.page('Auth page', {
      ...queryParams,
      emailParam,
      redirectTo,
      referrer,
    });
  }, [searchParams, emailParam, redirectTo]);

  const {
    signInWithEmail,
    signInWithGoogle,
    signInWithGitHub,
    signInWithHandshake,
    loading: authLoading,
    error: _authError,
  } = useAuthActions({
    role: role ?? 'candidate',
    product: 'interviewer',
    ...(redirectTo ? { redirectTo } : {}),
    referralCode: referralCode ?? undefined,
  });

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const cookieValue = document.cookie
          .split('; ')
          .find(row => row.startsWith('invitedCustomInterviewsTemplateId='))
          ?.split('=')[1];

        // Fallback to the default soft-skills interview template if the cookie expired/not found.
        const id = cookieValue || getDefaultInterviewTemplateId();
        const response = await fetch(`/api/interviewer/custom-interviews-templates/public/${id}`);
        if (!response.ok) {
          throw new Error('Failed to fetch information about the custom interview template');
        }
        const data = await response.json();
        setTemplate(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };
    fetchTemplate();
  }, []);
  const validateEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/;
    return regex.test(email);
  };
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    // Reset validation state if user starts typing after seeing an error
    if (!isValidEmail) {
      setIsValidEmail(true);
    }
  };
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateEmail(email)) {
      setIsValidEmail(false);
      return;
    }
    try {
      analytics.track('Sign In Attempt', {
        method: 'email',
        email,
      });
      await signInWithEmail(email);
      setShowLinkSent(true);
    } catch (err) {
      console.error('Email sign-in error:', err instanceof Error ? err.message : 'An unexpected error occurred.');
    }
    // For emails, we call maybeAddReferral() after the the OTP has been verified in TokenSent
  };

  const handleGoogleSignIn = async () => {
    try {
      analytics.track('Sign In Attempt', {
        method: 'google',
      });
      await signInWithGoogle();
    } catch (err) {
      console.error('Google sign-in error:', err instanceof Error ? err.message : 'An unexpected error occurred.');
    }
  };

  const handleGitHubSignIn = async () => {
    try {
      analytics.track('Sign In Attempt', {
        method: 'github',
      });
      await signInWithGitHub();
    } catch (err) {
      console.error('GitHub sign-in error:', err instanceof Error ? err.message : 'An unexpected error occurred.');
    }
  };

  const handleHandshakeSignIn = async () => {
    try {
      analytics.track('Sign In Attempt', {
        method: 'handshake',
      });
      await signInWithHandshake();
    } catch (err) {
      console.error('Handshake sign-in error:', err instanceof Error ? err.message : 'An unexpected error occurred.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-black" />
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div>
        Error:
        {error}
      </div>
    ); // TODO: Replace this with a proper error component
  }
  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-white relative overflow-hidden">
      {/* Centered Content */}
      <div className="relative z-10 flex items-center justify-center w-full max-w-5xl px-4 gap-12">
        {/* Info Text */}
        <div className="w-[470px] inline-flex flex-col justify-start items-start gap-6">
          <div className="self-stretch flex flex-col justify-start items-start gap-3">
            <div className="self-stretch justify-start text-black text-header-2xl font-['Noi_Grotesk']">
              Train AI. Get paid.
              <br />
              Work remotely.
            </div>
          </div>
          <div className="self-stretch flex flex-col justify-start items-start gap-3">
            <div className="self-stretch inline-flex justify-start items-center gap-3">
              <div className="w-6 h-6 relative overflow-hidden">
                <div className="w-[17px] h-[17px] left-[3.50px] top-[3.50px] absolute">
                  <Check className="w-full h-full" />
                </div>
              </div>
              <div className="justify-start text-black text-body-lg font-medium font-['Noi_Grotesk'] leading-normal">No AI experience needed</div>
            </div>
            <div className="self-stretch inline-flex justify-start items-center gap-3">
              <div className="w-6 h-6 relative overflow-hidden">
                <div className="w-[17px] h-[17px] left-[3.50px] top-[3.50px] absolute">
                  <Check className="w-full h-full" />
                </div>
              </div>
              <div className="justify-start text-black text-body-lg font-medium font-['Noi_Grotesk'] leading-normal">Work from anywhere</div>
            </div>
            <div className="self-stretch inline-flex justify-start items-center gap-3">
              <div className="w-6 h-6 relative overflow-hidden">
                <div className="w-[17px] h-[17px] left-[3.50px] top-[3.50px] absolute">
                  <Check className="w-full h-full" />
                </div>
              </div>
              <div className="justify-start text-black text-body-lg font-medium font-['Noi_Grotesk'] leading-normal">Competitive hourly rates</div>
            </div>
            <div className="self-stretch inline-flex justify-start items-center gap-3">
              <div className="w-6 h-6 relative overflow-hidden">
                <div className="w-[17px] h-[17px] left-[3.50px] top-[3.50px] absolute">
                  <Check className="w-full h-full" />
                </div>
              </div>
              <div className="justify-start text-black text-body-lg font-medium font-['Noi_Grotesk'] leading-normal">Free AI Trainer certification</div>
            </div>
            <div className="self-stretch inline-flex justify-start items-center gap-3">
              <div className="w-6 h-6 relative overflow-hidden">
                <div className="w-[17px] h-[17px] left-[3.50px] top-[3.50px] absolute">
                  <Check className="w-full h-full" />
                </div>
              </div>
              <div className="justify-start text-black text-body-lg font-medium font-['Noi_Grotesk'] leading-normal">Open to all educational backgrounds</div>
            </div>
          </div>
        </div>

        {/* Login Modal */}
        <div className="w-[330px] mx-auto">
          {showLinkSent
            ? (
                <TokenSent setShowLinkSent={setShowLinkSent} email={email} redirectTo={redirectTo} />
              )
            : (
                <SignUpForm
                  authError={_authError}
                  authLoading={authLoading}
                  email={email}
                  isValidEmail={isValidEmail}
                  isSignInWithHandshakeEnabled={isSignInWithHandshakeEnabled}
                  handleEmailChange={handleEmailChange}
                  handleEmailSubmit={handleEmailSubmit}
                  signInWithGoogle={handleGoogleSignIn}
                  signInWithGitHub={handleGitHubSignIn}
                  signInWithHandshake={handleHandshakeSignIn}
                />
              )}
        </div>
      </div>
    </div>
  );
}

export default function AuthPageV2() {
  return (
    <Suspense>
      <AuthPageV2Content />
    </Suspense>
  );
}
